/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { format } from '@msdyn365-commerce/retail-proxy';

import { IEvePriceCalculatorData } from './eve-price-calculator.data';
import { IEvePriceCalculatorProps, IZonePriceTypeData } from './eve-price-calculator.props.autogenerated';
import { EvePriceCalculatorInput } from './components/EvePriceCalculatorInput';
import { EvePriceCalculatorSelect } from './components/EvePriceCalculatorSelect';
import { EvePriceCalculatorButton } from './components/EvePriceCalculatorButton';
import { EvePriceCalculatorCheckbox } from './components/EvePriceCalculatorCheckbox';
import { EvePriceCalculatorResultsRow } from './components/EvePriceCalculatorResultsRow';
import { IZonePricingEntity } from '../../actions/DataServiceEntities.g';
import { validateSelect, validateAllInputs } from './utils/Validations';
import {
    calculateCasePriceFob,
    calculateExciseTax,
    calculateLandedCostFob,
    calculateLandedCostRetail,
    calculateMarkup,
    calculatePricePerCaseRetail,
    calculateSubtotalFob,
    calculateSubtotalRetail,
    ouncesToMilliliters,
    litersToMilliliters
} from './utils/Calculations';

export interface IEvePriceCalculatorViewProps extends IEvePriceCalculatorProps<IEvePriceCalculatorData> {
    className: string;
    form: React.ReactNode;
    results: React.ReactNode;
    heading: React.ReactNode;
    helpText: React.ReactNode;
}

interface IEvePriceCalculatorState {
    pricePerCaseInput: string;
    isFobOrRetailCalculation: string;
    taxType: string;
    zone: string;
    bottlesPerCase: string;
    sizeOfBottles: string;
    bottleUnitOfMeasure: string;
    isSpecialOrder: boolean;
    containsInputError: boolean;
    containsSelectError: boolean;
    pricePerCaseAmount: number;
    incomingFreight: number;
    landedCost: number;
    markupAmount: number;
    subTotal: number;
    exciseTax: number;
    outgoingFreightAmount: number;
    specialAmount: number;
    casePrice: number;
    zoneData: IZonePriceTypeData[];
}

/**
 *
 * EvePriceCalculator component
 * @extends {React.PureComponent<IEvePriceCalculatorProps<IEvePriceCalculatorData>>}
 */
class EvePriceCalculator extends React.PureComponent<IEvePriceCalculatorProps<IEvePriceCalculatorData>, IEvePriceCalculatorState> {
    // Base class name
    private readonly _className = 'eve-price-calculator';

    // State variable names for form entry
    private readonly _pricePerCaseInputName = 'pricePerCaseInput';
    private readonly _isFobOrRetailCalculationName = 'isFobOrRetailCalculation';
    private readonly _taxTypeName = 'taxType';
    private readonly _zoneName = 'zone';
    private readonly _bottlesPerCaseName = 'bottlesPerCase';
    private readonly _sizeOfBottlesName = 'sizeOfBottles';
    private readonly _bottleUnitOfMeasureName = 'bottleUnitOfMeasure';
    private readonly _isSpecialOrderName = 'isSpecialOrder';

    // Button class names
    private readonly _computeButtonName = 'compute';
    private readonly _resetButtonName = 'reset';

    // Default state form input and results values
    private _defaultFormValues = {
        isFobOrRetailCalculation: this.props.resources.fobOrRetailDefaultOptionText,
        taxType: this.props.resources.taxTypeDefaultOptionText,
        zone: this.props.resources.zoneDefaultOptionText,
        bottleUnitOfMeasure: this.props.resources.uomDefaultOptionText,
        pricePerCaseInput: '',
        bottlesPerCase: '',
        sizeOfBottles: '',
        isSpecialOrder: false,
        containsInputError: true,
        containsSelectError: true,
        pricePerCaseAmount: 0,
        incomingFreight: 0,
        landedCost: 0,
        markupAmount: 0,
        subTotal: 0,
        exciseTax: 0,
        outgoingFreightAmount: 0,
        specialAmount: 0,
        casePrice: 0
    };

    constructor(props: IEvePriceCalculatorProps<IEvePriceCalculatorData>) {
        super(props);

        this.state = {
            ...this._defaultFormValues,
            zoneData: []
        };
        this._onChange = this._onChange.bind(this);
        this._getOptions = this._getOptions.bind(this);
        this._handleOnSubmit = this._handleOnSubmit.bind(this);
        this._handleOnReset = this._handleOnReset.bind(this);
        this._getZonePricesList = this._getZonePricesList.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        this.setState({ zoneData: this._getZonePricesList() || [] });
    }

    /**
     * To handle paragraph change.
     * @param event - To handle text change event.
     * @public
     */
    public handleGuideTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.helpText = event.target.value;
    };

    /**
     * Method to be called on heading change.
     * @param event - Content Edit Event.
     * @public
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles changes to the checkbox, input or select fields and sets state accordingly.
     * @param name State variable name to be changed
     * @param value Value that will be set to the state variable passed in
     */
    private _onChange(name: string, value: string): void {
        const stateValue = name === this._isSpecialOrderName ? !this.state[`${name}`] : value;
        this.setState({ ...this.state, [name]: stateValue });
    }

    /**
     * Handles the form submission and calculates the results
     */
    private _handleOnSubmit = (): void => {
        this._calculatesResultsValues();
    };

    /**
     * Handles the form reset
     */
    private _handleOnReset = (): void => {
        this._resetFormValues();
    };

    /**
     * Calculates the results
     */
    private _calculatesResultsValues = (): void => {
        const {
            zone,
            isSpecialOrder,
            pricePerCaseInput,
            isFobOrRetailCalculation,
            taxType,
            sizeOfBottles,
            bottlesPerCase,
            bottleUnitOfMeasure
        } = this.state;

        const taxTypeRate = this._getTaxTypeRate(taxType);
        const convertedSizeOfBottles = this._getSizeOfBottlesInMilliliters(sizeOfBottles, bottleUnitOfMeasure);
        const exciseTaxAmount = calculateExciseTax(convertedSizeOfBottles, taxTypeRate, bottlesPerCase);
        const incomingFreightAmount = this._getIncomingFreightAmount(zone);
        const specialAmount = isSpecialOrder ? this.props.config.specialOrderFee || 0 : 0;
        const outgoingFreightAmount = this.props.config.outgoingFreightFee || 0;
        let pricePerCaseAmount = 0;
        let landedCostAmount = 0;
        let markupAmount = 0;
        let subTotalAmount = 0;
        let casePriceAmount = 0;

        if (isFobOrRetailCalculation === this.props.resources.fobOptionText) {
            pricePerCaseAmount = parseFloat(pricePerCaseInput);
            landedCostAmount = calculateLandedCostFob(pricePerCaseAmount, incomingFreightAmount);
            markupAmount = calculateMarkup(this.props.config.markupPercentage || 0, landedCostAmount);
            subTotalAmount = calculateSubtotalFob(landedCostAmount, markupAmount);
            casePriceAmount = calculateCasePriceFob(subTotalAmount, outgoingFreightAmount, specialAmount, exciseTaxAmount);
        } else {
            casePriceAmount = parseFloat(pricePerCaseInput);
            subTotalAmount = calculateSubtotalRetail(casePriceAmount, outgoingFreightAmount, specialAmount, exciseTaxAmount);
            landedCostAmount = calculateLandedCostRetail(subTotalAmount, this.props.config.markupPercentage || 0);
            pricePerCaseAmount = calculatePricePerCaseRetail(landedCostAmount, incomingFreightAmount);
            markupAmount = calculateMarkup(this.props.config.markupPercentage || 0, landedCostAmount);
        }

        this.setState({
            pricePerCaseAmount: pricePerCaseAmount,
            incomingFreight: incomingFreightAmount,
            landedCost: landedCostAmount,
            markupAmount: markupAmount,
            subTotal: subTotalAmount,
            exciseTax: exciseTaxAmount,
            outgoingFreightAmount: outgoingFreightAmount,
            specialAmount: specialAmount,
            casePrice: casePriceAmount
        });
    };

    /**
     * Gets the incoming freight fee for the chosen zone
     * @param zone Zone chosen in the form
     * @returns Incoming freight fee
     */
    private _getIncomingFreightAmount = (zone: string): number => {
        // const selectedZone = this.state.zoneData.find(z => z.Zone === zone);
        // return selectedZone && selectedZone.Value ? selectedZone.Value : 0;
        const selectedZone = this.state.zoneData.find(z => z.zoneName === zone);
        return selectedZone && selectedZone.fee ? selectedZone.fee : 0;
    };

    /**
     * Gets the tax type rate for the chosen tax type
     * @param taxType Tax type chosen in the form
     * @returns Tax type rate
     */
    private _getTaxTypeRate = (taxType: string): number => {
        // May be able to combine with the getIncomingFreightAmount method depending on how the zone data comes from the api call
        const selectedTaxType = this.props.config.taxTypes && this.props.config.taxTypes?.find(type => type.name === taxType);
        return selectedTaxType && selectedTaxType.rate ? selectedTaxType.rate : 0;
    };

    /**
     * Resets the state variables to default values
     */
    private _resetFormValues = (): void => {
        this.setState({
            ...this._defaultFormValues
        });
    };

    /**
     * Converts the bottle size entered to milliliters
     * @param bottleSize Bottle size as string entered from form
     * @param unitOfMeasure Bottle unit of measure from form
     * @returns Bottle size in milliliters as a number
     */
    private _getSizeOfBottlesInMilliliters = (bottleSize: string, unitOfMeasure: string): number => {
        const {
            resources: { ounceUomOptionText, literUomOptionText }
        } = this.props;

        if (unitOfMeasure === ounceUomOptionText) {
            return ouncesToMilliliters(bottleSize);
        }
        if (unitOfMeasure === literUomOptionText) {
            return litersToMilliliters(bottleSize);
        }
        return parseFloat(bottleSize);
    };

    /**
     * Gets the options for the chosen select element
     * @param optionType Select element that needs to populate options
     * @returns Array of options to be displayed in the select element
     */
    private _getOptions = (optionType: string): string[] => {
        const {
            resources: {
                fobOrRetailLabel,
                taxTypeLabel,
                zoneLabel,
                bottleUnitOfMeasureLabel,
                fobOptionText,
                retailOptionText,
                milliliterUomOptionText,
                literUomOptionText,
                ounceUomOptionText,
                uomDefaultOptionText,
                zoneDefaultOptionText,
                taxTypeDefaultOptionText,
                fobOrRetailDefaultOptionText
            },
            config: { taxTypes /*, zonePriceTypes*/ }
        } = this.props;

        let options: string[] = [];
        switch (optionType) {
            case fobOrRetailLabel:
                options = [fobOrRetailDefaultOptionText, fobOptionText, retailOptionText];
                return options;

            case bottleUnitOfMeasureLabel:
                options = [uomDefaultOptionText, milliliterUomOptionText, literUomOptionText, ounceUomOptionText];
                return options;

            case taxTypeLabel:
                options = [taxTypeDefaultOptionText];
                // Add tax type names to list if they exist in the array from Site Builder and name is not empty
                const taxTypesList =
                    taxTypes && taxTypes.length > 0
                        ? taxTypes.flatMap(taxType => {
                              if (!taxType.name) {
                                  return [];
                              }
                              return taxType.name;
                          })
                        : [];
                options = options.concat(taxTypesList);
                return options;

            case zoneLabel:
                options = [zoneDefaultOptionText];
                const zones = this.state.zoneData;
                const zonePriceTypesList = zones && zones.length > 0 ? zones.flatMap(zonePriceType => zonePriceType.zoneName || '') : [];
                options = options.concat(zonePriceTypesList);
                return options;

            default:
                return [];
        }
    };

    /**
     * Gets zone prices from data
     */
    private _getZonePricesList = (): IZonePriceTypeData[] => {
        //@ts-ignore
        const zonesResults: IZonePricingEntity[] = this.props.data.zones.result ? this.props.data.zones.result.zones : [];
        const zonesConfig: IZonePriceTypeData[] = this.props.config.zonePriceTypes || [];
        const zones =
            zonesResults && zonesResults.length > 0
                ? zonesResults.flatMap(zoneResult => {
                      // Check that the Zone name exists and that the value exists while also being greater than $0.00
                      if (!zoneResult.Zone || (!!zoneResult.Value && zoneResult.Value < 0)) {
                          return [];
                      }
                      const zone: IZonePriceTypeData = {
                          zoneName: zoneResult.Zone,
                          fee: zoneResult.Value
                      };
                      return zone;
                  })
                : [];
        const zonesConfigResults: IZonePriceTypeData[] =
            zonesConfig &&
            zonesConfig.flatMap(zone => {
                if (!zone.zoneName || (!!zone.fee && zone.fee < 0)) {
                    return [];
                }
                const zoneEntity: IZonePriceTypeData = {
                    zoneName: zone.zoneName,
                    fee: zone.fee
                };
                return zoneEntity;
            });
        const zoneData = [...zones, ...zonesConfigResults];
        return zoneData;
    };

    /**
     * Renders the heading text that's added to the module in Site Builder
     * @returns React.ReactElement with the heading text
     */
    private readonly _renderHeading = (): React.ReactElement => {
        const {
            config: { heading },
            context: { request }
        } = this.props;

        if (!heading || !heading.text) {
            return <></>;
        }

        return (
            <Msdyn365.Text
                className={`${this._className}__heading`}
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: request }}
            />
        );
    };

    /**
     * Renders the helper text that's added to the module in Site Builder
     * @returns React.ReactElement with the heading text
     */
    private readonly _renderHelpText = (): React.ReactElement => {
        const {
            config: { helpText },
            context: { request }
        } = this.props;

        if (!helpText) {
            return <></>;
        }

        return (
            <Msdyn365.RichTextComponent
                text={helpText}
                className={`${this._className}__help-text`}
                editProps={{ onEdit: this.handleGuideTextChange, requestContext: request }}
            />
        );
    };

    /**
     * Renders the form that will allow the user to input data to calculate the landed cost
     * @returns ReactNode that displays the form
     */
    private _renderForm = (): React.ReactNode => {
        const {
            resources: {
                pricePerCaseLabel,
                bottlesPerCaseLabel,
                sizeOfBottlesLabel,
                fobOrRetailLabel,
                taxTypeLabel,
                zoneLabel,
                bottleUnitOfMeasureLabel,
                specialLabel,
                computeButtonText,
                resetButtonText,
                invalidNumberErrorText,
                fobOrRetailDefaultOptionText,
                uomDefaultOptionText,
                zoneDefaultOptionText,
                taxTypeDefaultOptionText
            }
        } = this.props;

        const {
            pricePerCaseInput,
            isFobOrRetailCalculation,
            bottlesPerCase,
            sizeOfBottles,
            bottleUnitOfMeasure,
            isSpecialOrder,
            taxType,
            zone,
            containsInputError,
            containsSelectError
        } = this.state;
        const stateSelectVarList = [isFobOrRetailCalculation, taxType, zone, bottleUnitOfMeasure];
        const optionDefaultsList = [fobOrRetailDefaultOptionText, uomDefaultOptionText, zoneDefaultOptionText, taxTypeDefaultOptionText];
        const stateInputVarList = [pricePerCaseInput, bottlesPerCase, sizeOfBottles];
        this.setState({
            containsSelectError: !validateSelect(stateSelectVarList, optionDefaultsList),
            containsInputError: !validateAllInputs(stateInputVarList)
        });
        return (
            <div className={`${this._className}-display__form`}>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorInput
                        label={pricePerCaseLabel}
                        inputName={this._pricePerCaseInputName}
                        value={pricePerCaseInput}
                        errorText={invalidNumberErrorText}
                        onChange={this._onChange}
                    />
                    <EvePriceCalculatorSelect
                        label={fobOrRetailLabel}
                        selectName={this._isFobOrRetailCalculationName}
                        value={isFobOrRetailCalculation}
                        getOptions={this._getOptions}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorSelect
                        label={taxTypeLabel}
                        selectName={this._taxTypeName}
                        value={taxType}
                        getOptions={this._getOptions}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorSelect
                        label={zoneLabel}
                        selectName={this._zoneName}
                        value={zone}
                        getOptions={this._getOptions}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorInput
                        label={bottlesPerCaseLabel}
                        inputName={this._bottlesPerCaseName}
                        value={bottlesPerCase}
                        errorText={invalidNumberErrorText}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorInput
                        label={sizeOfBottlesLabel}
                        inputName={this._sizeOfBottlesName}
                        value={sizeOfBottles}
                        errorText={invalidNumberErrorText}
                        onChange={this._onChange}
                    />
                    <EvePriceCalculatorSelect
                        label={bottleUnitOfMeasureLabel}
                        selectName={this._bottleUnitOfMeasureName}
                        value={bottleUnitOfMeasure}
                        getOptions={this._getOptions}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row`}>
                    <EvePriceCalculatorCheckbox
                        label={specialLabel}
                        checkboxName={this._isSpecialOrderName}
                        value={isSpecialOrder}
                        onChange={this._onChange}
                    />
                </div>
                <div className={`${this._className}-display__form-row-button`}>
                    <EvePriceCalculatorButton
                        label={computeButtonText}
                        buttonName={this._computeButtonName}
                        isDisabled={containsInputError || containsSelectError}
                        onClick={this._handleOnSubmit}
                    />
                    <EvePriceCalculatorButton
                        label={resetButtonText}
                        buttonName={this._resetButtonName}
                        isDisabled={false}
                        onClick={this._handleOnReset}
                    />
                    {this.props.slots.help}
                </div>
            </div>
        );
    };

    /**
     * Renders the results from the calculation or the default values
     * @returns ReactNode that displays the results
     */
    private _renderResults = (): React.ReactNode => {
        const {
            resources: {
                fobPricePerCaseText,
                incomingFreightText,
                landedCostText,
                markupText,
                subTotalText,
                exciseTaxText,
                outgoingFreightText,
                plusSpecialText,
                casePriceText
            },
            config: { markupPercentage },
            context: {
                cultureFormatter: { formatCurrency }
            }
        } = this.props;

        const {
            pricePerCaseAmount,
            landedCost,
            exciseTax,
            casePrice,
            subTotal,
            incomingFreight,
            markupAmount,
            outgoingFreightAmount,
            specialAmount
        } = this.state;
        const className = `${this._className}-display__results`;
        const formattedMarkupText = format(markupText, markupPercentage ? markupPercentage : 0);

        return (
            <div className={`${className}`}>
                <EvePriceCalculatorResultsRow label={fobPricePerCaseText} value={pricePerCaseAmount} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={incomingFreightText} value={incomingFreight} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={landedCostText} value={landedCost} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={formattedMarkupText} value={markupAmount} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={subTotalText} value={subTotal} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={exciseTaxText} value={exciseTax} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={outgoingFreightText} value={outgoingFreightAmount} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={plusSpecialText} value={specialAmount} formatCurrency={formatCurrency} />
                <EvePriceCalculatorResultsRow label={casePriceText} value={casePrice} formatCurrency={formatCurrency} />
            </div>
        );
    };

    public render(): JSX.Element | null {
        const viewProps: IEvePriceCalculatorViewProps = {
            ...this.props,
            className: this._className,
            form: this._renderForm(),
            results: this._renderResults(),
            heading: this._renderHeading(),
            helpText: this._renderHelpText()
        };
        return this.props.renderView(viewProps);
    }
}

export default EvePriceCalculator;
